import React, { useEffect, useState, useCallback } from "react";
import BrandLogo from "../components/BrandLogo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BRAND } from "../mock";
import {
  adminLogin,
  adminToken,
  downloadLeadsCsv,
  fetchLeads,
} from "../lib/api";
import { Search, Download, Lock, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import StudioUsersPanel from "../components/admin/StudioUsersPanel";
import StudioFeedPanel from "../components/admin/StudioFeedPanel";
import AdminMessagesPanel from "../components/admin/AdminMessagesPanel";
import AdminPerksPanel from "../components/admin/AdminPerksPanel";
import AdminAccountModal from "../components/admin/AdminAccountModal";
import AccountMenu from "../components/studio/AccountMenu";
import useHeartbeat from "../hooks/useHeartbeat";
import { adminMessagesUnreadCount, adminFetchMe, pingAdminHeartbeat } from "../lib/studioApi";

function LoginGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const tryLogin = async () => {
    if (!pw) return;
    setLoading(true);
    try {
      const { token } = await adminLogin(pw);
      adminToken.set(token);
      onUnlock();
    } catch (e) {
      toast.error("Wrong password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0f1e] px-6">
      <div className="w-full max-w-md bg-[#10162a] border border-white/5 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.4)] p-8">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-paul-yellow flex items-center justify-center">
            <Lock className="w-7 h-7 text-navy" />
          </div>
        </div>
        <h1 className="text-2xl font-extrabold text-white text-center">
          Admin Access
        </h1>
        <p className="text-center text-white/50 mt-2 text-sm">
          Enter password to manage inquiries & the private studio
        </p>
        <Input
          data-testid="admin-password-input"
          type="password"
          placeholder="Password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && tryLogin()}
          className="mt-6 h-12 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-paul-yellow/50 focus-visible:border-paul-yellow/60"
          autoFocus
          disabled={loading}
        />
        <Button
          data-testid="admin-unlock-btn"
          onClick={tryLogin}
          disabled={loading || !pw}
          className="mt-4 w-full h-12 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
          style={{ color: "#0b1535" }}
        >
          {loading ? "Checking..." : "Unlock"}
        </Button>
      </div>
    </div>
  );
}

function LeadDetail({ lead, open, onOpenChange }) {
  if (!lead) return null;
  const rows = [
    ["Name", lead.name],
    ["Email", lead.email],
    ["Phone", lead.phone || "—"],
    ["Music Link", lead.musicLink],
    ["Timeframe", lead.timeframe],
    ["Hold Up", lead.holdup],
    ["Project", lead.project],
    ["Goals", lead.goals],
    ["Anything Else", lead.anythingElse || "—"],
    ["Submitted", new Date(lead.submittedAt).toLocaleString()],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#10162a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold text-white">
            {lead.name}
          </DialogTitle>
        </DialogHeader>
        <div className="divide-y divide-white/5">
          {rows.map(([k, v]) => (
            <div key={k} className="py-3 grid grid-cols-[140px_1fr] gap-4">
              <div className="text-sm font-semibold text-white/50">{k}</div>
              <div className="text-white/90 whitespace-pre-wrap break-words">
                {v}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InquiriesPanel({ onUnauth }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchLeads()
      .then((d) => setLeads(d))
      .catch((e) => {
        if (e?.response?.status === 401) {
          onUnauth();
        } else {
          toast.error("Failed to load leads");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLeads()
      .then((d) => setLeads(d))
      .catch((e) => {
        if (e?.response?.status === 401) onUnauth();
      })
      .finally(() => setLoading(false));
  }, [onUnauth]);

  const filtered = leads.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q)
    );
  });

  const handleExport = async () => {
    try {
      await downloadLeadsCsv();
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-white">Inquiries</h2>
          <p className="text-white/50 text-sm mt-1">
            {leads.length} total submissions
          </p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              placeholder="Search by name, email, phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 w-72 bg-[#10162a] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
            />
          </div>
          <Button
            onClick={handleExport}
            className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
            style={{ color: "#0b1535" }}
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="bg-[#0f1322] rounded-xl shadow-sm border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-white/60">Name</TableHead>
              <TableHead className="text-white/60">Email</TableHead>
              <TableHead className="text-white/60">Phone</TableHead>
              <TableHead className="text-white/60">Submitted</TableHead>
              <TableHead className="text-right text-white/60">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="text-center text-white/40 py-10"
                >
                  {loading ? "Loading..." : "No submissions yet."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((l) => (
                <TableRow key={l.id} className="border-white/5 hover:bg-white/[0.03]">
                  <TableCell className="font-semibold text-white">
                    {l.name}
                  </TableCell>
                  <TableCell className="text-white/85">{l.email}</TableCell>
                  <TableCell className="text-white/85">{l.phone || "—"}</TableCell>
                  <TableCell className="text-white/50">
                    {new Date(l.submittedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelected(l)}
                      className="border-white/15 bg-transparent text-paul-yellow hover:bg-paul-yellow/10 hover:text-paul-yellow"
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <LeadDetail
        lead={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}

export default function Admin() {
  const [unlocked, setUnlocked] = useState(() => !!adminToken.get());
  const [tab, setTab] = useState("inquiries");
  const [unread, setUnread] = useState(0);
  const [adminMe, setAdminMe] = useState(null);
  const [accountModal, setAccountModal] = useState({ open: false, mode: "profile", focusAvatar: false });

  // Heartbeat for admin "online" presence (every 60s while unlocked)
  useHeartbeat(pingAdminHeartbeat, 60000, unlocked);

  const onUnauth = () => {
    adminToken.clear();
    setUnlocked(false);
    toast.error("Session expired. Please log in again.");
  };

  const handleLogout = () => {
    adminToken.clear();
    setUnlocked(false);
  };

  useEffect(() => {
    if (!unlocked) return undefined;
    const tick = () => {
      adminMessagesUnreadCount().then(setUnread).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 8000);
    adminFetchMe().then(setAdminMe).catch(() => {});
    return () => clearInterval(id);
  }, [unlocked]);

  if (!unlocked) return <LoginGate onUnlock={() => setUnlocked(true)} />;

  const unreadBadge = unread > 9 ? "9+" : unread > 0 ? String(unread) : null;

  return (
    <div className="min-h-screen bg-[#0b0f1e] text-white">
      <header className="bg-[#10162a]/95 backdrop-blur border-b border-white/5 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo variant="yellow" className="h-9" />
            <span className="text-sm text-white/30">/ Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <AccountMenu
              user={adminMe}
              onLogout={handleLogout}
              onViewProfile={() => setAccountModal({ open: true, mode: "profile", focusAvatar: false })}
              onChangePic={() => setAccountModal({ open: true, mode: "settings", focusAvatar: true })}
              onSettings={() => setAccountModal({ open: true, mode: "settings", focusAvatar: false })}
              theme="dark"
              testIdPrefix="admin-account-menu"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#10162a] border border-white/5 mb-6">
            <TabsTrigger
              value="inquiries"
              data-testid="admin-tab-inquiries"
              className="text-white/70 data-[state=active]:bg-paul-yellow data-[state=active]:text-navy font-semibold"
            >
              Inquiries
            </TabsTrigger>
            <TabsTrigger
              value="users"
              data-testid="admin-tab-users"
              className="text-white/70 data-[state=active]:bg-paul-yellow data-[state=active]:text-navy font-semibold"
            >
              Studio Users
            </TabsTrigger>
            <TabsTrigger
              value="feed"
              data-testid="admin-tab-feed"
              className="text-white/70 data-[state=active]:bg-paul-yellow data-[state=active]:text-navy font-semibold"
            >
              Studio Feed
            </TabsTrigger>
            <TabsTrigger
              value="perks"
              data-testid="admin-tab-perks"
              className="text-white/70 data-[state=active]:bg-paul-yellow data-[state=active]:text-navy font-semibold"
            >
              Perks
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              data-testid="admin-tab-messages"
              className="text-white/70 data-[state=active]:bg-paul-yellow data-[state=active]:text-navy font-semibold gap-2"
            >
              Messages
              {unreadBadge && (
                <span
                  data-testid="admin-tab-messages-badge"
                  className="bg-red-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                >
                  {unreadBadge}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries">
            <InquiriesPanel onUnauth={onUnauth} />
          </TabsContent>
          <TabsContent value="users">
            <StudioUsersPanel />
          </TabsContent>
          <TabsContent value="feed">
            <StudioFeedPanel />
          </TabsContent>
          <TabsContent value="perks">
            <AdminPerksPanel />
          </TabsContent>
          <TabsContent value="messages">
            <AdminMessagesPanel />
          </TabsContent>
        </Tabs>
      </main>

      <AdminAccountModal
        key={accountModal.open ? `${accountModal.mode}-${accountModal.focusAvatar}` : "closed"}
        open={accountModal.open}
        mode={accountModal.mode}
        focusAvatar={accountModal.focusAvatar}
        onOpenChange={(o) => setAccountModal((s) => ({ ...s, open: o }))}
        profile={adminMe}
        onUpdate={setAdminMe}
      />
    </div>
  );
}
